import { HttpClient, HttpEventType, HttpHeaders, HttpProgressEvent } from '@angular/common/http';
import { catchError, concatMap, filter, map, retry, takeUntil, tap, throttleTime } from 'rxjs/operators';
import { FileTransactionStatusEnum } from '../interfaces/file-transaction-status.enum';
import { forkJoin, Observable, throwError } from 'rxjs';
import { FileTransactionModel } from '../interfaces/file-transaction.model';
import { inject } from '@angular/core';


export interface IUploadRequest {
  key: string;
  parts: IUploadRequestPart[];
}

export interface IUploadRequestPart {
  index: number;
  url: string;
  length: number;
}

export abstract class UploadService<T> {
  abstract requestUploadUrl: string;
  abstract confirmUploadUrl: string;

  protected http = inject(HttpClient);

  protected abstract prepareUploadData(partResponse: any, request: IUploadRequest, data: T): any;

  public upload(data: T, fileModel: FileTransactionModel) {
    return this.requestForUpload(fileModel.fileName, fileModel.fileSize).pipe(
      takeUntil(fileModel.destroy$),
      concatMap(request => {
        return forkJoin(this.prepareUploadParts(request.parts, fileModel))
          .pipe(
            map(response => {
                return {
                  uploadData: response,
                  request: request
                }
              }
            )
          );
      }),
      map((response) => this.prepareUploadData(response.uploadData, response.request, data)),
      concatMap(response => this.sendConfirmRequest(response)),
      catchError((error) => {
        this.processingUploadError(fileModel);
        return throwError(() => error);
      }),
      tap(() => this.processingUploadConfirmed(fileModel))
    );
  }

  protected requestForUpload(fileName: string, size: number): Observable<IUploadRequest> {
    return this.http.put<IUploadRequest>(this.requestUploadUrl, {fileName, size});
  }

  protected uploadPart(part: IUploadRequestPart, chunk: Blob): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/octet-stream',
      'x-skip-interceptor': ''
    });

    return this.http.put(part.url, chunk, {
      responseType: 'blob',
      reportProgress: true,
      observe: 'events',
      headers
    }).pipe(
      throttleTime(1000, undefined, {trailing: true, leading: false}),
    );
  }

  private processingUploadProgress(event: HttpProgressEvent, part: IUploadRequestPart, fileProgress: FileTransactionModel): void {
    if (!event || !event.loaded || !event.total) {
      return;
    }

    fileProgress.setUploadedSize(part.index, event.loaded);
  }

  private processingUploadConfirmed(fileProgressModel: FileTransactionModel): void {
    fileProgressModel.percentage.set(100);
    fileProgressModel.status = FileTransactionStatusEnum.confirmed;
  }

  private processingUploadError(fileProgressModel: FileTransactionModel): void {
    fileProgressModel.status = FileTransactionStatusEnum.error;
  }

  private prepareUploadParts(parts: IUploadRequestPart[], fileProgressModel: FileTransactionModel): Observable<any>[] {
    let start = 0;
    let end = 0;
    return parts.map(part => {
        start = end;
        end = start + part.length;
        const chunk: Blob = fileProgressModel.file.slice(start, end, fileProgressModel.fileType);

        return this.uploadPart(part, chunk).pipe(
          takeUntil(fileProgressModel.destroy$),
          tap((event) => {
            if (event.type === HttpEventType.UploadProgress) {
              this.processingUploadProgress(event, part, fileProgressModel);
            }
          }),
          retry({count: 5, delay: 1500}),
          filter(response => response.type === HttpEventType.Response),
          map(response => {
              return {
                value: response.headers.get('ETag').replace(/(^")|("$)/g, ''),
                index: part.index
              }
            }
          )
        )
      }
    );
  }

  private sendConfirmRequest(data: any): Observable<any> {
    return this.http.put(this.confirmUploadUrl, data);
  }
}
